import { Component, EventEmitter, Input, Output } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AdminDodTransferService } from "../../../../api/services/admin-dod-transfer.service";
import { DodEndPoint } from "../../../../api/models/dod-end-point";
import { DodTransferDocument } from "../../../../api/models/dod-transfer-document";
import { DodTransferPlan } from "../../../../api/models/dod-transfer-plan";
import { DodTransferItem } from "../../../../api/models/dod-transfer-item";
import { DodTransferApplyResult } from "../../../../api/models/dod-transfer-apply-result";

const MAX_FILE_BYTES = 20 * 1024 * 1024;
const CONFIRM_WORD = "IMPORT";

// The item types a plan can contain, in the order they are shown.
export const DOD_TRANSFER_TYPES = [
  { value: "AggregateType", label: "Aggregate types" },
  { value: "EndPoint", label: "Endpoints" },
  { value: "SubData", label: "Sub-data" },
  { value: "Column", label: "Columns" },
  { value: "Dimension", label: "Dimensions" },
  { value: "Measure", label: "Measures" },
];

type ActionFilter = "changes" | "all" | "guarded" | "blocked";

// Export and import of the DoD definition. Export builds a key-based JSON file (no ids, no Fabric workspace/notebook ids);
// import is a four-step flow - choose the file, preview exactly what would happen (nothing is written), acknowledge any
// guarded overwrites, and apply in one all-or-nothing transaction. Import only adds and updates; nothing is deleted.
@Component({
  selector: "admin-dod-transfer",
  templateUrl: "./admin-dod-transfer.component.html",
})
export class AdminDodTransferComponent {
  // The endpoints to offer for export (from the editor's loaded definition).
  @Input() endPoints: DodEndPoint[] = [];
  // Raised after a successful import so the editor reloads, and when the admin asks to see validation.
  @Output() imported = new EventEmitter<void>();
  @Output() openValidation = new EventEmitter<void>();

  // ---- export ----
  public selectedEndPoints = new Set<string>();
  public includeRetired = false;
  public exporting = false;
  public lastExport: DodTransferDocument | null = null;

  // ---- import: file step ----
  public dragging = false;
  public fileName = "";
  public fileProblems: string[] = [];
  public document: DodTransferDocument | null = null;

  // ---- import: review (0 = file, 1 = preview, 2 = confirm, 3 = result) ----
  public step = 0;
  public previewing = false;
  public plan: DodTransferPlan | null = null;
  public previewError = "";
  public actionFilter: ActionFilter = "changes";
  public itemSearch = "";
  public expanded = new Set<string>();
  public acknowledged = new Set<string>();
  public typedConfirmation = "";
  public applying = false;
  public applyError = "";
  public result: DodTransferApplyResult | null = null;

  public readonly types = DOD_TRANSFER_TYPES;
  public readonly confirmWord = CONFIRM_WORD;
  public readonly stepNames = ["File", "Preview", "Confirm", "Result"];

  constructor(private transferService: AdminDodTransferService, private snackBar: MatSnackBar) {}

  // =============================== export =====================================================================
  get visibleEndPoints(): DodEndPoint[] {
    return (this.endPoints || []).filter((e) => this.includeRetired || !e.retired);
  }

  toggleEndPoint(key: string, on: boolean) { on ? this.selectedEndPoints.add(key) : this.selectedEndPoints.delete(key); }
  selectAll() { this.visibleEndPoints.forEach((e) => this.selectedEndPoints.add(e.key)); }
  clearEndPoints() { this.selectedEndPoints.clear(); }

  get exportScope(): string {
    return this.selectedEndPoints.size === 0 ? "all endpoints" : `${this.selectedEndPoints.size} selected endpoint${this.selectedEndPoints.size === 1 ? "" : "s"}`;
  }

  export() {
    this.exporting = true;
    this.lastExport = null;
    this.transferService.adminDodTransferExport({ endPoints: Array.from(this.selectedEndPoints), includeRetired: this.includeRetired }).subscribe({
      next: (doc) => {
        this.exporting = false;
        this.lastExport = doc;
        const env = (doc.exportedFromEnvironment || "env").toLowerCase().replace(/[^a-z0-9]+/g, "-");
        this.download(doc, `dod-definition-${env}-${new Date().toISOString().substring(0, 10)}.json`);
      },
      error: (e) => {
        this.exporting = false;
        this.snackBar.open(this.errorText(e, "Could not build the export"), "Close", { duration: 10000 });
      },
    });
  }

  summary(doc: DodTransferDocument): string {
    const eps = doc.endPoints || [];
    const columns = eps.reduce((n, e) => n + (e.columns || []).length + (e.subData || []).reduce((m, s) => m + (s.columns || []).length, 0), 0);
    const parts = [
      `${eps.length} endpoint${eps.length === 1 ? "" : "s"}`,
      `${columns} columns`,
      `${eps.reduce((n, e) => n + (e.measures || []).length, 0)} measures`,
      `${eps.reduce((n, e) => n + (e.dimensions || []).length, 0)} dimensions`,
      `${eps.reduce((n, e) => n + (e.subData || []).length, 0)} sub-data`,
    ];
    return parts.join(", ");
  }

  private download(content: unknown, fileName: string) {
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // =============================== import: step 1, the file ===================================================
  onFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) this.readFile(input.files[0]);
    input.value = "";
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) this.readFile(event.dataTransfer.files[0]);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragging = true;
  }

  private readFile(file: File) {
    this.resetImport();
    this.fileName = file.name;
    if (file.size > MAX_FILE_BYTES) { this.fileProblems = [`The file is ${(file.size / 1048576).toFixed(1)} MB; the limit is 20 MB.`]; return; }
    if (!/\.json$/i.test(file.name)) { this.fileProblems = ["Choose a .json file exported from this page."]; return; }

    const reader = new FileReader();
    reader.onerror = () => { this.fileProblems = ["The file could not be read."]; };
    reader.onload = () => {
      let parsed: any;
      try { parsed = JSON.parse(String(reader.result)); }
      catch { this.fileProblems = ["The file is not valid JSON."]; return; }

      const problems: string[] = [];
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) problems.push("The file does not look like a DoD definition export.");
      else {
        if (parsed.formatVersion !== 1) problems.push(`Unsupported format version${parsed.formatVersion === undefined ? " (missing)" : ` ${parsed.formatVersion}`}; this page reads version 1.`);
        for (const key of ["endPoints", "aggregateTypes"]) {
          if (parsed[key] !== undefined && !Array.isArray(parsed[key])) problems.push(`"${key}" must be a list.`);
        }
        if (!Array.isArray(parsed.endPoints) && !Array.isArray(parsed.aggregateTypes)) problems.push("The file contains no endpoints or aggregate types.");
      }
      this.fileProblems = problems;
      if (problems.length === 0) this.document = parsed as DodTransferDocument;
    };
    reader.readAsText(file);
  }

  get fileSummary(): string { return this.document ? this.summary(this.document) : ""; }

  resetImport() {
    this.step = 0;
    this.fileName = "";
    this.fileProblems = [];
    this.document = null;
    this.plan = null;
    this.result = null;
    this.previewError = "";
    this.applyError = "";
  }

  // =============================== import: preview ============================================================
  startReview() {
    if (!this.document || this.fileProblems.length > 0) return;
    this.step = 1;
    this.preview();
  }

  preview() {
    this.previewing = true;
    this.previewError = "";
    this.transferService.adminDodTransferImportPreview({ document: this.document }).subscribe({
      next: (plan) => {
        this.previewing = false;
        this.plan = plan;
        this.step = 1;
        this.acknowledged.clear();
        this.typedConfirmation = "";
        this.expanded.clear();
        this.actionFilter = plan.blocked > 0 ? "blocked" : "changes";
      },
      error: (e) => {
        this.previewing = false;
        this.previewError = this.errorText(e, "The preview failed");
      },
    });
  }

  get items(): DodTransferItem[] { return this.plan?.items || []; }

  itemsOfType(type: string): DodTransferItem[] {
    const q = this.itemSearch.trim().toLowerCase();
    return this.items.filter((i) => {
      if (i.type !== type) return false;
      if (q && !(i.key || "").toLowerCase().includes(q) && !(i.endPoint || "").toLowerCase().includes(q)) return false;
      switch (this.actionFilter) {
        case "changes": return i.action !== "Unchanged";
        case "guarded": return i.action === "Update" && i.hasGuardedChanges;
        case "blocked": return i.action === "Blocked";
        default: return true;
      }
    });
  }

  countOfType(type: string, action?: string): number {
    return this.items.filter((i) => i.type === type && (!action || i.action === action)).length;
  }

  get visibleCount(): number { return this.types.reduce((n, t) => n + this.itemsOfType(t.value).length, 0); }

  toggleExpanded(item: DodTransferItem) {
    const id = item.ackKey;
    this.expanded.has(id) ? this.expanded.delete(id) : this.expanded.add(id);
  }

  expandAll(open: boolean) {
    this.expanded.clear();
    if (open) this.types.forEach((t) => this.itemsOfType(t.value).forEach((i) => this.expanded.add(i.ackKey)));
  }

  badgeClass(action: string): string {
    switch (action) {
      case "Create": return "badge badge-success";
      case "Update": return "badge badge-primary";
      case "Blocked": return "badge badge-danger";
      default: return "badge badge-secondary";
    }
  }

  impactText(item: DodTransferItem): string {
    const i = item.impact;
    if (!i) return "";
    const parts: string[] = [];
    if (i.layouts > 0) parts.push(`${i.layouts} saved layout${i.layouts === 1 ? "" : "s"}`);
    if (i.planTypes > 0) parts.push(`${i.planTypes} plan type${i.planTypes === 1 ? "" : "s"}`);
    return parts.length > 0 ? `Used by ${parts.join(", ")}` : "";
  }

  downloadReport() {
    if (!this.plan) return;
    this.download(this.plan, `dod-import-preview-${new Date().toISOString().substring(0, 10)}.json`);
  }

  // =============================== import: confirm ============================================================
  get guardedItems(): DodTransferItem[] { return this.items.filter((i) => i.action === "Update" && i.hasGuardedChanges); }
  get guardedInUse(): DodTransferItem[] { return this.guardedItems.filter((i) => i.impact && i.impact.inUse); }
  get allAcknowledged(): boolean { return this.guardedItems.every((i) => this.acknowledged.has(i.ackKey)); }
  get needsTypedConfirmation(): boolean { return this.guardedInUse.length > 0; }
  get typedConfirmationOk(): boolean { return !this.needsTypedConfirmation || this.typedConfirmation.trim() === CONFIRM_WORD; }
  get canApply(): boolean { return !!this.plan && this.plan.canApply && this.allAcknowledged && this.typedConfirmationOk && !this.applying; }

  toggleAcknowledged(item: DodTransferItem, on: boolean) { on ? this.acknowledged.add(item.ackKey) : this.acknowledged.delete(item.ackKey); }
  acknowledgeAll(on: boolean) {
    this.acknowledged.clear();
    if (on) this.guardedItems.forEach((i) => this.acknowledged.add(i.ackKey));
  }

  guardedChanges(item: DodTransferItem) { return (item.changes || []).filter((c) => c.guarded); }

  goToConfirm() { this.step = 2; }
  backToPreview() { if (this.step === 2) this.step = 1; }

  // =============================== import: apply ==============================================================
  apply() {
    if (!this.canApply || !this.plan) return;
    this.applying = true;
    this.applyError = "";
    this.transferService
      .adminDodTransferImportApply({ document: this.document, planHash: this.plan.planHash, acknowledgedKeys: Array.from(this.acknowledged) })
      .subscribe({
        next: (result) => {
          this.applying = false;
          this.result = result;
          this.step = 3;
          this.imported.emit();
        },
        error: (e) => {
          this.applying = false;
          this.applyError = this.errorText(e, "The import failed. Nothing was changed.");
          // The definition moved on since the preview: send the admin back to a fresh preview.
          if (/Preview the import again/i.test(this.applyError)) this.preview();
        },
      });
  }

  get resultErrors() { return (this.result?.issues || []).filter((i) => i.severity === 1); }
  get resultWarnings() { return (this.result?.issues || []).filter((i) => i.severity === 0); }

  private errorText(e: any, fallback: string): string {
    const body = e && e.error;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && (body.title || body.message)) return body.title || body.message;
    return fallback;
  }
}
